import {
  GetStreamData,
  ReportsDeepResearchStreamTask,
  ReportsDeepResearchTaskResult,
} from '@/app/api/copilot/reports/research/types';
import { QueryCopilotData, QueryCopilotResponseData } from '@/pages/api/map-events/query-copilot.types';
import { Formatter } from '../utils/Formatter';
import { logSentryException as _logSentryException } from '../utils/sentry';
import {
  LinkedDocument,
  ParsedDateExtraction,
  ParsedLine,
  formatSelectedDates,
  isParsedChunk,
  isParsedDateExtraction,
  isParsedLinkedDocuments,
} from './api.copilot.utils';
import { ROUTE, axiosInstance } from './axiosInstance';

export interface CopilotQueryReportsParams extends Omit<QueryCopilotData, 'mode'> {
  onAnswerToken(chunk: string): void;
  onCitationToken(chunk: string): void;
  onLinkedDocuments(docs: LinkedDocument[]): void;
  onDateExtraction(data: ParsedDateExtraction): void;
  onError(err: Error): void;
}

const logSentryException = ({ message, data }: { message: string; data?: Record<string, any> }) =>
  _logSentryException({
    message,
    place: 'api.copilot.queryReports',
    data,
  });

const handleReportsStreamErrorResponse = async (
  res: Response,
  { onError }: Pick<CopilotQueryReportsParams, 'onError'>,
) => {
  const clonedRes = res.clone();
  try {
    // For HTTP errors that return JSON
    const errorText = await clonedRes.text();
    let errorMessage = `Server error: ${clonedRes.status}`;

    try {
      const errorJson = JSON.parse(errorText);
      errorMessage = errorJson.error?.message || errorJson.error || errorText;
    } catch {
      errorMessage = errorText || errorMessage;
    }

    logSentryException({ message: errorMessage });
    onError(new Error(errorMessage));
  } catch {
    logSentryException({ message: `Server error: ${clonedRes.status}` });
    onError(new Error(`Server error: ${clonedRes.status}`));
  }
};

const tryInvokeTaskIdFromDemoResponse = (responseText: string): ReportsDeepResearchStreamTask | null => {
  try {
    const responseJson: ReportsDeepResearchStreamTask = JSON.parse(responseText);
    if (responseJson.task_id) {
      return responseJson;
    }
    return null;
  } catch {
    return null;
  }
};

/** Reports copilot query */
export const copilot = {
  /** README: Move to separate "/copilot/query" API */
  queryMap: async (params: QueryCopilotData) => {
    const { data } = await axiosInstance.post<QueryCopilotResponseData>(ROUTE.COPILOT.QUERY_MAP, params);
    return data;
  },

  /** README: http://10.9.64.73/docs#/default/reports_endpoint_reports_post */
  queryReports: async ({
    query,
    org_id,
    user_id,
    chat_id,
    subset_path,
    selected_dates,
    onAnswerToken,
    onCitationToken,
    onLinkedDocuments,
    onDateExtraction,
    onError,
  }: CopilotQueryReportsParams) => {
    const res = await fetch(`/api${ROUTE.COPILOT.QUERY_REPORTS}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query,
        org_id,
        user_id,
        chat_id: chat_id.replace(/_/g, ''), // As a fix for service issue with symbols in chat_id
        subset_path,
        user_timezone: Formatter.getUserTimeZone(),
        selected_dates: formatSelectedDates(selected_dates),
      } satisfies QueryCopilotData),
    });

    if (!res.ok) {
      await handleReportsStreamErrorResponse(res, { onError });
      return;
    }
    if (!res.body) {
      onError(new Error('No response body'));
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let partial = '';
    let task: ReportsDeepResearchStreamTask | null = null;
    let pauseProcessing = false;

    let done = false;
    while (!done) {
      const { value, done: doneReading } = await reader.read();
      if (doneReading) {
        done = doneReading;
        break;
      }

      if (value) {
        partial += decoder.decode(value, { stream: true });
        let parts = partial.split('\n');

        partial = parts.pop() || '';

        // Demo case, check JSON response for task_id
        task = tryInvokeTaskIdFromDemoResponse(partial);
        if (task) {
          reader.cancel();
          done = true;
          break;
        }

        for (let line of parts) {
          if (!line.trim()) continue;
          try {
            // Handle and read stream messages
            const data: ParsedLine = JSON.parse(line);
            if (pauseProcessing) {
              continue;
            }

            if (isParsedChunk(data)) {
              if (data.event === 'answer_token') {
                onAnswerToken(data.token);
              }
              if (data.event === 'citations_token') {
                onCitationToken(data.token);
              }
            }

            // Date extraction event
            if (isParsedDateExtraction(data) && data.awaiting_confirmation) {
              onDateExtraction(data);
              pauseProcessing = true;
              continue;
            }

            // Linked documents (kind of citations)
            if (isParsedLinkedDocuments(data)) {
              if (data.linked_documents) {
                onLinkedDocuments(data.linked_documents);
              }
            }

            if ('error' in data && data.error) {
              const errMsg = typeof data.error === 'object' ? JSON.stringify(data.error) : data.error;
              onError(new Error(errMsg));
              logSentryException({
                message: errMsg,
                data: {
                  payload: JSON.stringify({ query, org_id, user_id, chat_id, subset_path }),
                },
              });
            }
          } catch (err) {
            console.error('Bad chunk:', line);
            logSentryException({
              message: 'Bad chunk:',
              data: {
                line,
              },
            });
            onError(err as Error);
          }
        }
      }
    }

    if (task?.task_id) {
      return copilot
        .getDeepResearchTaskStream({
          task_id: task.task_id,
          taskMode: task.mode,
          type: 'stream',
          query,
          onAnswerToken,
          onError,
        })
        .then(async () => new Promise((resolve) => setTimeout(resolve, 1000))) // small delay to ensure all tokens are processed
        .then(async () => {
          onAnswerToken('\n\n**[RESULTS]**\n');

          return copilot
            .getDeepResearchTaskResult({
              task_id: task.task_id,
              taskMode: task.mode,
              query,
            })
            .then((result: ReportsDeepResearchTaskResult) => {
              onAnswerToken(result.final_text || '');
            })
            .catch((err) => {
              onError(err);
            });
        });
    }
  },

  getDeepResearchTaskStream: async ({
    task_id,
    taskMode,
    type,
    query,
    onAnswerToken,
    onError,
  }: Pick<CopilotQueryReportsParams, 'onAnswerToken' | 'onError'> & {
    task_id: ReportsDeepResearchStreamTask['task_id'];
    taskMode?: ReportsDeepResearchStreamTask['mode'];
    type: GetStreamData['type'];
    query: GetStreamData['query'] & CopilotQueryReportsParams['query'];
  }) => {
    const url = new URL(`/api${ROUTE.COPILOT.QUERY_REPORTS_DEEP_RESEARCH}`, window.location.origin);
    url.searchParams.append('task_id', task_id);
    url.searchParams.append('type', type);
    url.searchParams.append('query', query);
    if (taskMode) {
      url.searchParams.append('mode', taskMode);
    }
    const res = await fetch(url.toString(), {
      method: 'GET',
    });

    if (!res.ok) {
      await handleReportsStreamErrorResponse(res, { onError });
      return;
    }
    if (!res.body) {
      onError(new Error('No response body'));
      return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let partial = '';
    let currentEvent = '';

    let done = false;
    while (!done) {
      const { value, done: doneReading } = await reader.read();
      if (doneReading) {
        done = doneReading;
        break;
      }

      if (value) {
        partial += decoder.decode(value, { stream: true });
        let parts = partial.split('\n');

        partial = parts.pop() || '';

        for (let line of parts) {
          // Skip empty lines and keepalive messages
          if (!line.trim() || line.trim() === ': keepalive') continue;

          try {
            // Parse SSE format: "event: event_type" or "data: json"
            if (line.startsWith('event:')) {
              currentEvent = line.substring(6).trim();
            } else if (line.startsWith('data:')) {
              const jsonData = line.substring(5).trim();

              if (!jsonData) continue;

              const data = JSON.parse(jsonData);

              // Handle reasoning_delta events - these contain the actual content
              if (currentEvent === 'reasoning_delta' && data.delta) {
                // All delta tokens go to answer by default
                onAnswerToken(data.delta);
              }
              if (currentEvent === 'text_delta' && data.delta) {
                // All delta tokens go to answer by default
                onAnswerToken(data.delta);
              }

              // Handle status updates
              if (currentEvent === 'status_update' && data.status) {
                console.log('Deep research status:', data.status);
              }

              // Handle errors
              if (data.error) {
                const errMsg = typeof data.error === 'object' ? JSON.stringify(data.error) : data.error;
                onError(new Error(errMsg));
                logSentryException({
                  message: errMsg,
                  data: {
                    task_id,
                    type,
                  },
                });
              }
            }
          } catch (err) {
            console.error('Bad SSE chunk:', line);
            logSentryException({
              message: 'Bad SSE chunk:',
              data: {
                line,
                currentEvent,
              },
            });
            onError(err as Error);
          }
        }
      }
    }
  },

  getDeepResearchTaskResult: async ({
    task_id,
    taskMode,
    query,
  }: {
    task_id: ReportsDeepResearchStreamTask['task_id'];
    taskMode: ReportsDeepResearchStreamTask['mode'];
    query: GetStreamData['query'] & CopilotQueryReportsParams['query'];
  }) => {
    const url = new URL(`/api${ROUTE.COPILOT.QUERY_REPORTS_DEEP_RESEARCH}`, window.location.origin);
    url.searchParams.append('task_id', task_id);
    url.searchParams.append('type', 'result');
    url.searchParams.append('mode', taskMode);
    url.searchParams.append('query', query);

    const res = await fetch(url.toString(), {
      method: 'GET',
    });

    if (!res.ok) {
      const errorText = await res.text();
      throw new Error(`Server error: ${res.status} - ${errorText}`);
    }

    const result: ReportsDeepResearchTaskResult = await res.json();
    return result;
  },
};
